import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  Link,
  List,
  ListItem,
  ListItemText,
  Typography,
} from '@mui/material'
import {
  FacebookPage,
  MutationCreateAutomationsArgs,
  MutationUpdateAutomationsArgs,
  RetailerAccount,
  Maybe,
} from '@promoboxx/graphql-gateway-types'
import { Formik } from 'formik'
import I18n from 'i18n-js'
import { useSnackbar } from 'notistack'
import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react'
import { useSelector } from 'react-redux'
import { makeStyles } from 'tss-react/mui'

import analytics from '@src/analytics'
import manageBrandsPaths from '@src/components/brands/manageBrandsPaths'
import { useAutomations } from '@src/lib/useAutomations'
import { useRefinerSurvey } from '@src/lib/useRefinerSurvey'
import { currentRetailerAccountSelector } from '@src/stores/app/selectors'
import InstagramIcon from '@src/svgs/legacy/instagram.svg?react'

interface IGAutomationFormValues {
  selectedInstagramAutomations: (Maybe<string> | undefined)[]
  isAllBrandsSelected: boolean
}

interface IGAutomationProps {
  onClose: (values: IGAutomationFormValues) => void
  isIframe?: boolean
  brandName?: string
  brandId?: string
}

export interface IGAutomationImperativeRef {
  (): {
    initialSelectedInstagramAutomations: IGAutomationFormValues['selectedInstagramAutomations']
    retailerHasNoFacebookPages: boolean
    buttonClicked: 'unknown' | 'submit' | 'cancel'
  }
}

const useStyles = makeStyles()((theme) => ({
  noFacebookPagesTextPadding: {
    paddingBottom: theme.spacing(4),
  },
  dialogContentTopDivider: {
    paddingTop: theme.spacing(2),
  },
  dialogTitle: {
    textAlign: 'center',
  },
  stickTopForIframe: {
    backgroundColor: theme.palette.background.paper,
    position: 'sticky',
    top: 0,
    zIndex: 1,
  },
  stickBottomForIframe: {
    backgroundColor: theme.palette.background.paper,
    position: 'sticky',
    bottom: 0,
    zIndex: 1,
  },
  igIconSpacer: {
    marginRight: theme.spacing(4),
  },
}))

const IGAutomation = forwardRef<IGAutomationImperativeRef, IGAutomationProps>(
  ({ onClose, isIframe, brandName, brandId }, ref) => {
    const [retailerHasNoFacebookPages, setRetailerHasNoFacebookPages] =
      useState(true)

    const params = manageBrandsPaths.index.useParams()

    const currentRetailerAccount: RetailerAccount = useSelector(
      currentRetailerAccountSelector,
    )

    const { enqueueSnackbar } = useSnackbar()

    const {
      getAutomations,
      createAutomations,
      updateAutomations,
      automations,
      getAutomationsLoading,
    } = useAutomations(currentRetailerAccount)

    const { classes, cx } = useStyles()

    const buttonClickedRef = useRef<'unknown' | 'submit' | 'cancel'>('unknown')

    const retailerFacebookPagesByRetailerId = useSelector(
      (state: {
        retailersFacebookPages: { byRetailerId: Record<string, FacebookPage[]> }
      }) => state.retailersFacebookPages.byRetailerId,
    )
    const retailerFacebookPagesStatus = useSelector(
      (state: {
        retailersFacebookPages: { facebookPagesStatus: 'pending' | 'complete' }
      }) => state.retailersFacebookPages.facebookPagesStatus,
    )
    useEffect(() => {
      getAutomations(['retailer', 'automationable'])
    }, [getAutomations, params.retailerAccountSlug])

    useEffect(() => {
      setRetailerHasNoFacebookPages(
        !currentRetailerAccount.retailers?.some(
          (retailer) =>
            !!retailerFacebookPagesByRetailerId[retailer.id]?.length,
        ),
      )
    }, [retailerFacebookPagesByRetailerId, currentRetailerAccount.retailers])

    const loading =
      getAutomationsLoading && retailerFacebookPagesStatus !== 'complete'

    let retailersWithIGAutoshare =
      currentRetailerAccount.retailers?.filter(
        (retailer) => retailer.brand?.auto_share_instagram,
      ) || []

    // If coming from the campaign page, we want to move the brand to the top of the list of checkboxes
    if (isIframe) {
      const brandThatCreatedCampaign = retailersWithIGAutoshare.find(
        (retailer) => retailer.brand?.id === brandId,
      )
      if (brandThatCreatedCampaign) {
        retailersWithIGAutoshare = retailersWithIGAutoshare.filter(
          (retailer) => retailer.brand?.id !== brandId,
        )
        retailersWithIGAutoshare.unshift(brandThatCreatedCampaign)
      }
    }

    const instagramAutomationsThatAreEnabled =
      automations
        ?.filter(
          (automation) =>
            automation.category === 'instagram' && automation.enabled,
        )
        .map((automation) => automation.retailer_id) || []

    const { refiner } = useRefinerSurvey()

    const trackIgAutomation = (type: string) => {
      analytics.track({
        // All of the events from product in this component are 'Click' so hardcoding this.
        event: 'Click',
        type,
        data: {
          source: isIframe ? 'IG Share Flow' : 'Billboard',
          feature: 'IG Auto Share',
        },
      })
    }

    useImperativeHandle(ref, () => {
      return () => {
        return {
          retailerHasNoFacebookPages,
          initialSelectedInstagramAutomations:
            instagramAutomationsThatAreEnabled,
          buttonClicked: buttonClickedRef.current,
        }
      }
    })

    return (
      <Formik<IGAutomationFormValues>
        initialValues={{
          selectedInstagramAutomations: instagramAutomationsThatAreEnabled,
          isAllBrandsSelected: false,
        }}
        enableReinitialize
        onSubmit={async (values, { setSubmitting }) => {
          try {
            const automationsToCreate: MutationCreateAutomationsArgs = {
              params: [],
            }
            const automationsToUpdate: MutationUpdateAutomationsArgs = {
              params: [],
            }

            for (const retailer of retailersWithIGAutoshare) {
              // Get all fb pages for id
              const retailerFacebookPages =
                retailerFacebookPagesByRetailerId[retailer.id] || []
              // For each fb page, check if it's in the automations
              if (
                values.selectedInstagramAutomations.includes(
                  retailer.id.toString(),
                )
              ) {
                for (const retailerFacebookPage of retailerFacebookPages) {
                  const automation = automations?.find(
                    (automation) =>
                      automation.automationable_id ===
                        retailerFacebookPage.id.toString() &&
                      automation.category === 'instagram' &&
                      automation.retailer_id?.toString() ===
                        retailer.id.toString(),
                  )
                  // If it is disabled, enable it
                  if (automation && automation.id) {
                    automationsToUpdate.params.push({
                      automationId: automation.id,
                      state: 'enabled',
                    })
                  }
                  // If it doesn't exist, create it
                  else {
                    automationsToCreate.params.push({
                      retailerId: retailer.id,
                      automation: {
                        category: 'instagram',
                        automationableId: retailerFacebookPage.id,
                        automationableType: 'FacebookPage',
                      },
                    })
                  }
                }
              } else {
                // If it is enabled but as been unchecked (it won't be in values.selectedInstagramAutomations), disable it
                for (const retailerFacebookPage of retailerFacebookPages) {
                  const automation = automations?.find(
                    (automation) =>
                      automation.automationable_id ===
                        retailerFacebookPage.id.toString() &&
                      automation.category === 'instagram' &&
                      automation.retailer_id?.toString() ===
                        retailer.id.toString(),
                  )
                  if (automation && automation.id) {
                    automationsToUpdate.params.push({
                      automationId: automation.id,
                      state: 'disabled',
                    })
                  }
                }
              }
            }
            if (automationsToCreate.params.length) {
              await createAutomations(automationsToCreate)
            }
            if (automationsToUpdate.params.length) {
              await updateAutomations(automationsToUpdate)
            }
            enqueueSnackbar(I18n.t('igAutoShare.successMessage'), {
              variant: 'success',
            })
          } catch (err) {
            console.error(err)
            enqueueSnackbar(I18n.t('igAutoShare.errorMessage'), {
              variant: 'error',
            })
          }
          setSubmitting(false)
          onClose(values)

          if (!isIframe && values.selectedInstagramAutomations.length === 0) {
            refiner('showForm', 'why-no-ig-autoshare')
          }
        }}
      >
        {({ values, setFieldValue, handleSubmit, resetForm, isSubmitting }) => {
          const handleClose = () => {
            resetForm()
            onClose(values)
          }

          const handleToggleBrand = (retailerId: string) => {
            values.selectedInstagramAutomations.includes(retailerId.toString())
              ? setFieldValue(
                  'selectedInstagramAutomations',
                  values.selectedInstagramAutomations.filter(
                    (id) => id !== retailerId,
                  ),
                )
              : setFieldValue('selectedInstagramAutomations', [
                  ...values.selectedInstagramAutomations,
                  retailerId,
                ])
          }
          const handleToggleAllBrands = () => {
            setFieldValue('isAllBrandsSelected', !values.isAllBrandsSelected)
            if (!values.isAllBrandsSelected) {
              setFieldValue(
                'selectedInstagramAutomations',
                retailersWithIGAutoshare.map((retailer) =>
                  retailer.id.toString(),
                ),
              )
            } else {
              setFieldValue('selectedInstagramAutomations', [])
            }
          }
          return (
            <>
              {!retailerHasNoFacebookPages && (
                <DialogTitle
                  className={cx(classes.dialogTitle, {
                    [classes.stickTopForIframe]: isIframe,
                  })}
                >
                  <Box display="flex" margin={1}>
                    <InstagramIcon
                      height="1.6em"
                      className={classes.igIconSpacer}
                    />
                    {isIframe
                      ? I18n.t('igAutoShare.iFrameTitle', { brandName })
                      : I18n.t('igAutoShare.dialogTitle')}
                  </Box>
                  <Typography variant="body1">
                    <Link
                      href="https://support.promoboxx.com/hc/en-us/articles/15363080292123-What-is-Auto-Share-"
                      target="_blank"
                    >
                      {I18n.t('igAutoShare.whatIsInstagramAutoShare')}
                    </Link>
                  </Typography>
                </DialogTitle>
              )}
              {loading && (
                <Box display="flex" justifyContent="center">
                  <CircularProgress />
                </Box>
              )}
              {!loading && retailerHasNoFacebookPages ? (
                <DialogContent
                  dividers
                  className={classes.noFacebookPagesTextPadding}
                >
                  <Box textAlign="center">
                    {I18n.t('igAutoShare.noFacebookPagesConnected')}
                    <br />
                    <br />
                    <Link
                      href={`/#/${currentRetailerAccount.slug}/all/settings?tab=social_accounts`}
                    >
                      {I18n.t('igAutoShare.addThemHere')}
                    </Link>
                  </Box>
                </DialogContent>
              ) : (
                <>
                  <DialogContent
                    dividers
                    className={classes.dialogContentTopDivider}
                  >
                    <List>
                      {retailersWithIGAutoshare?.map((retailer) => (
                        <ListItem
                          key={`brand-${retailer.brand?.name}`}
                          button
                          divider
                          disabled={isSubmitting}
                          onClick={() => {
                            handleToggleBrand(retailer.id.toString())
                            analytics.track({
                              event: 'Click',
                              type: 'Clicked on Checkbox for Brand',
                              data: {
                                source: isIframe
                                  ? 'IG Share Flow'
                                  : 'Billboard',
                                brandName: retailer.brand?.name,
                                feature: 'IG Auto Share',
                              },
                            })
                          }}
                        >
                          <Checkbox
                            disabled={isSubmitting}
                            checked={values.selectedInstagramAutomations.includes(
                              retailer.id.toString(),
                            )}
                          />
                          <ListItemText primary={retailer.brand?.name} />
                        </ListItem>
                      ))}
                    </List>
                    <DialogContentText variant="subtitle2">
                      * {I18n.t('igAutoShare.note')}
                    </DialogContentText>
                  </DialogContent>
                  <DialogActions
                    className={
                      isIframe ? classes.stickBottomForIframe : undefined
                    }
                  >
                    <FormControlLabel
                      control={
                        <Checkbox
                          disabled={isSubmitting}
                          onChange={() => {
                            handleToggleAllBrands()
                            trackIgAutomation(
                              'Clicked on Select All Brands Checkbox',
                            )
                          }}
                          checked={values.isAllBrandsSelected}
                        />
                      }
                      label={I18n.t('igAutoShare.selectAllBrands')}
                    />
                    <Button
                      disabled={isSubmitting}
                      onClick={() => {
                        buttonClickedRef.current = 'cancel'
                        handleClose()
                        trackIgAutomation('Clicked Cancel Button')
                        if (
                          !isIframe &&
                          instagramAutomationsThatAreEnabled.length === 0
                        ) {
                          refiner('showForm', 'why-no-ig-autoshare')
                        }
                      }}
                      variant="outlined"
                    >
                      {I18n.t('shared.cancel')}
                    </Button>
                    <Button
                      disabled={
                        isSubmitting ||
                        !values.selectedInstagramAutomations.length
                      }
                      onClick={() => {
                        buttonClickedRef.current = 'submit'
                        handleSubmit()
                        trackIgAutomation('Clicked Submit Button')
                      }}
                      color="primary"
                      variant="contained"
                    >
                      {I18n.t('igAutoShare.enable')}
                    </Button>
                  </DialogActions>
                </>
              )}
            </>
          )
        }}
      </Formik>
    )
  },
)

export default IGAutomation
